import { useEffect, useState } from "react";
import { PasswordInput } from "../../../../components/ui/inputs/PasswordInput";
import localization from "../../../../config/localization";
import { Button } from "../../../../components/ui/Button";
import { Header4, Header6 } from "../../../../components/ui/Headers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Validations } from "../../../../models/constants/validations";
import { useLocation, useNavigate } from "react-router-dom";
import userService from "../../services/user.service";
import { toastService as toast } from "../../../../components/ui/toast/toast.service";
import { RESET_PASSWORD_TYPE_FORGOT, RESET_PASSWORD_TYPE_ONE_CLICK, RESET_PASSWORD_TYPE_WHATSAPP, RESET_PASSWORD_TYPES, ResetPasswordType } from "../../../auth/models/resetPasswordType";
import { fetchUserProfile } from "../../actions/userProfileActions";
import { useAppDispatch } from "../../../../store/hooks";
import { changeLoaderState, changeLoginModalState } from "../../../../reducers/appStateReducer";
import { localStorageService } from "../../../../services";
import { authService } from "../../../auth";
import { useSelector } from "react-redux";
import { selectUserProfile } from "../../selectors/userProfileSelectors";
import { RegistrationType } from "../../../../models/constants/registrationType";
import { updateUserIsOneTimePasswordChanged } from "../../reducers/userProfileReducer";

interface ResetPasswordProps {
    resetPasswordType?: ResetPasswordType;
}

export function ResetPassword({ resetPasswordType }: ResetPasswordProps) {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { state } = useLocation();
    const showBackButton = (state as any)?.showBackButton;
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null);

    const resetPasswordTypeData =
        resetPasswordType ??
        state?.resetPasswordType ??
        RESET_PASSWORD_TYPES.find(t => t.type === resetPasswordType);

    const { title, description } = resetPasswordTypeData as ResetPasswordType ?? {
        title: localization.reset_password,
        description: localization.forgot_password_label,
    };

    const profile = useSelector(selectUserProfile);

    const isAllowedForReset = profile && ((profile.registrationType && profile.registrationType.toLowerCase() === RegistrationType.ManualOrWhatsApp.toLowerCase() && profile.isOneClickRegistration && !profile.isOneTimePasswordChanged)

        || (profile?.isOneClickRegistration && !profile?.isOneTimePasswordChanged &&
            (profile.registrationType === RegistrationType.ManualOrWhatsApp || profile.registrationType.toLowerCase() === RegistrationType.OneClick.toLowerCase())))

    useEffect(() => {
        if (resetPasswordType?.type !== RESET_PASSWORD_TYPE_FORGOT && !isAllowedForReset) {
            navigate("/");
        }
    }, []);

    useEffect(() => {
        if (showBackButton) {
            return () => {
                authService.updatePasswordChangeOneClick();
                dispatch(fetchUserProfile());
            };
        }
    })


    useEffect(() => {
        if (!showBackButton) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault();
                e.returnValue = "";
            };

            window.addEventListener("beforeunload", handleBeforeUnload);

            return () => {
                window.removeEventListener("beforeunload", handleBeforeUnload);
            };
        }
    }, [showBackButton]);


    const handleBackButton = async () => {
        dispatch(changeLoaderState(true));
        await authService.updatePasswordChangeOneClick();
        dispatch(fetchUserProfile());

        setTimeout(() => {
            dispatch(changeLoaderState(false));
            navigate("/");
        }, 1000);
    }

    const checkValidation = () => {
        setPasswordError(null);
        setConfirmPasswordError(null);

        let isValid = true;

        if (!password) {
            setPasswordError("error_Password");
            isValid = false;
        }

        else if (!Validations.password.regex.test(password)) {
            setPasswordError("valid_password_must_contain");
            isValid = false;
        }

        if (!confirmPassword) {
            setConfirmPasswordError("error_ConfirmPassword");
            isValid = false;
        }

        else if (password !== confirmPassword) {
            setConfirmPasswordError("new_password_not_match");
            isValid = false;
        }

        return isValid;
    }

    const clearPasswordErrorOnFocus = () => {
        setPasswordError((prev) => (prev ? null : prev));
    };

    const clearConfirmPasswordErrorOnFocus = () => {
        setConfirmPasswordError((prev) => (prev ? null : prev));
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setPassword(value);
        setPasswordError((prev) => (value ? null : prev));
    }

    const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setConfirmPassword(value);
        setConfirmPasswordError((prev) => (value ? null : prev));
    }

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!checkValidation()) {
            return;
        }

        userService.resetPassword(password, confirmPassword).then((response) => {
            let type = resetPasswordTypeData?.type || state?.resetPasswordType;
            if (response.isSuccess) {
                toast.success(localization.password_changed);
                if (type === RESET_PASSWORD_TYPE_ONE_CLICK || type === RESET_PASSWORD_TYPE_WHATSAPP) {
                    dispatch(fetchUserProfile());
                    dispatch(changeLoaderState(true));
                    dispatch(updateUserIsOneTimePasswordChanged(true));
                    navigate("/");
                    dispatch(changeLoginModalState(true));
                    setTimeout(() => {
                        dispatch(changeLoaderState(false));
                    }, 1000);
                }
                else {
                    localStorageService.clear();
                    navigate("/");
                }

            }
        });
    }

    return (
        <div className={`${resetPasswordType?.type === "Forgot" ? "" : "p-4"}`}>
            <div className="flex justify-center px-4 sm:px-6 lg:px-8">
                <div className="w-full max-w-4xl mx-auto">
                    <div className="flex items-center gap-2 mt-5">
                        {showBackButton && (
                            <Button
                                type="button"
                                className="flex h-9 w-9 items-center justify-center rounded-full bg-[#0d0f19] text-white shadow-md"
                                onClick={async () => await handleBackButton()}
                            >
                                <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                            </Button>
                        )}

                        <Header4 className="text-white font-semibold uppercase tracking-wider">
                            {title}
                        </Header4>
                    </div>
                    <form onSubmit={handleSubmit}>

                        <div className="mt-5">
                            <Header6 className="text-white mb-5 font-semibold tracking-wider">
                                {description}
                            </Header6>
                        </div>
                        <div className="mt-4 space-y-3">
                            <PasswordInput
                                label={localization.new_password}
                                placeholder={localization.new_password}
                                value={password}
                                isRequired={true}
                                error={passwordError && localization.getString(passwordError)}
                                onChange={handlePasswordChange}
                                onFocus={clearPasswordErrorOnFocus}
                                maxLength={20}
                            />

                            <PasswordInput
                                label={localization.confirm_new_password}
                                placeholder={localization.confirm_new_password}
                                value={confirmPassword}
                                isRequired={true}
                                error={confirmPasswordError && localization.getString(confirmPasswordError)}
                                onChange={handleConfirmPasswordChange}
                                onFocus={clearConfirmPasswordErrorOnFocus}
                                maxLength={20}
                            />
                        </div>
                        <div className="mt-4">
                            <Button
                                type="submit"
                                className="mt-2 w-full py-4 rounded-lg text-white bg-primary-gradient uppercase tracking-wider hover:bg-primary-gradient/80 cursor-pointer"
                            >
                                {localization.submit}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}